import React, { useEffect, useState, useRef } from "react";
import { Button, Col, Container, Form, InputGroup, Nav, Row, Tab } from "react-bootstrap";
import axios from "axios";
import get from "lodash.get";
import { injectIntl } from "react-intl";
import PropTypes from 'prop-types';
import { isEmpty } from "lodash";
import { useDebouncedCallback } from "use-debounce";
import useI18n from "../../hooks/useI18n";
import { useNavigate, useLocation, useParams } from "react-router-dom";

// Services
import { userRequestHeaders } from '../../services/user';
import { toggleLoading, formatMoney, validate } from "../../services/helpers";
import {jobFetchJob, jobGetJob, jobSetGovidJob} from "../../services/job";

// Components
import RetrieveInvoiceModal from "../../components/RetrieveInvoiceModal";
import { far } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Helmet } from "react-helmet";
import messages from "./Dashboard.messages";
import useUserAccount from "../../hooks/useUserAccount";
import DashboardHeader from "../../components/DashboardHeader";
import OverpaidWizard from "../../components/OverpaidWizard";
import useSessionStorage from "../../hooks/useSessionStorage";
import useJobs from "../../hooks/useJobs";
import useSettings from "../../hooks/useSettings";
import DashboardPane from "../../components/DashboardPane";

const Dashboard = (props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { uuid } = useParams();
    const {
        userIsCompany, userIsAuthenticated, userIsLoggedIn, userShouldSwitchToCompany, uuidAuthenticate
    } = useUserAccount();
    const userAuthorized = userIsAuthenticated();
    const { showChatDisabledNotice } = useSettings();
    const [showRetrieveModal, setShowRetrieveModal] = useState(false);
    const [retrieveInvoiceError, setRetrieveInvoiceError] = useState({
        message: '',
        type: 'light'
    });
    const [dashboardMessage, setDashboardMessage] = useState({
        message: '',
        type: 'light'
    });
    const [govidJobs, setGovidJobs] = useState(false);
    const [currentTab, setCurrentTab] = useState("invoices");
    const [searchText, setSearchText] = useState("");
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [callback, setCallback] = useState(false);
    const [state, setState] = useState({
        jobid: 0,
        code: "",
        billnum: "",
        reference: "",
        postcode: "",
        jobidForAccountNumber: 0,
        codeForAccountNumber: "",
    });
    const [showOverpaidWizard, setShowOverpaidWizard] = useState(false);
    const [overpaidJob, setOverpaidJob] = useState(null);
    const [overpaidInformation, setOverpaidInformation] = useState({
        show: false,
        account: '',
        amount: '',
        name: '',
        fee: ''
    });
    const searchResultsRef = useRef(null); // Ref for scrolling to search results
    const { getLocale } = useI18n();
    const { getjobTokenUuid, clearjobTokenUuid } = useSessionStorage();
    const { fetchJobWithUuid } = useJobs();
    const userLocale = getLocale();
    const jobTokenUuid = getjobTokenUuid();

    let searchInUrlParams = false;

    // Govid search with debounced timeout
    const debounced = useDebouncedCallback((value) => {
        // When input has over 3 letters, init search
        if (value.length > 3) {
            setCurrentTab("search");
            getOpenInvoicesByGovid('search', value);
        }

        // When input is empty restore tabs
        if (value.length === 0) {
            setCurrentTab("invoices");
            getOpenInvoicesByGovid('invoices');
        }
    }, props.timeout ?? 1000);

    /**
     * Function for closing the Return payment wizard
     */
    const returnPaymentClose = () => {
        resetRetrieveInvoiceError();
        setShowOverpaidWizard(false);
        setOverpaidJob(null);
    };

    /**
     * Return payment wizard step 1
     * @param formData
     */
    const handleOverpaidJobSearch = (formData) => {
        // Reset errors and other notifications
        resetRetrieveInvoiceError();
        resetDashboardMessage();
        resetOverpaidInformation();

        // Get the form
        const form = document.getElementById('returnpayment-dpid-form');
        if (form !== undefined && form !== null && validate(form)) {
            // Get the case number and Online ID and form the dpid
            const { caseNumber, onlineId  } = formData;
            if (caseNumber !== '' && onlineId !== '') {
                // Fetch the job data and move to next step
                handleSubmit(`${caseNumber}-${onlineId}`);
            } else {
                setRetrieveInvoiceError({
                    message: lng(messages.unknownError),
                    type: 'danger'
                });
            }
        } else {
            setRetrieveInvoiceError({
                message: lng(messages.unknownError),
                type: 'danger'
            });
        }
    };

    // Translations function
    const lng = props.intl.formatMessage;


    // Get open invoices by user govid
    async function getOpenInvoicesByGovid(eventType = 'invoices', searchTerm = '') {
        setCurrentTab(eventType === 'linksearch' ? 'search' : eventType);
        setGovidJobs(false);

        // Get header information that might contain authorization details
        const headers = userRequestHeaders();

        let _govidJobs = false;
        let route = '/govidsearch';
        let queryParams = '';

        switch (eventType) {
            case "invoices":
                queryParams = '?include_paid=0&exclude_open=0&order=paydate&dir=DESC';
                break;
            case "archive":
                queryParams = '?include_paid=1&exclude_open=1&order=paydate&dir=DESC';
                break;
            case "search":
                queryParams = '?include_paid=1&exclude_open=0&order=paydate&dir=DESC&search=' + searchTerm;
                break;
            case "linksearch":
                route = '/linksearch';
                queryParams = '?include_paid=1&exclude_open=0&order=paydate&dir=DESC&search=' + searchTerm;
                break;
        }

        await axios({
            method: 'GET',
            url: process.env.REACT_APP_API_URL + '/online' + route + queryParams,
            headers: headers
        })
        .then(function (res) {

            // Could be empty body with 204 no content
            if (typeof res.data !== 'undefined' && typeof res.data.govidjob !== 'undefined' && res.data.govidjob !== null) {
                // set jobs
                setGovidJobs(res.data.govidjob);

                _govidJobs = res.data.govidjob;

                // save govid jobs to session storage, too
                jobSetGovidJob(_govidJobs);

                // scroll to the search results only when applicable
                if (searchInUrlParams && searchResultsRef.current) {
                    searchResultsRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            }
        })
        .catch(function (error) {
            if (error.response?.status === 401) {
                const alert = {message: lng(messages.authError), type: 'danger'};
                navigate("/logout", { state: { alert } });
            } else {
                setGovidJobs(error);
                _govidJobs = error;
                return Promise.reject(error);
            }
        })
        .then(function () {
            // Add empty array if no jobs found
            if (_govidJobs === false) {
                setGovidJobs([]);
            }
        });
    }

    useEffect(() => {
        const handleDpidSearch = (dpid) => {
            const [jobid, code] = dpid.split('-');
            setState({ ...state, jobid, code });
            setIsAuthenticating(false);
            // set Callback value to trigger UE
            setCallback('dpid');
        }
        // get dpid from session storage if user identified with an undirect link
        const dpid = sessionStorage.getItem('dpid');
        // if uuid exists, first authenticate the user
        if (uuid) {
            // loading animation on
            toggleLoading(true, lng(messages.openingInvoice) + "...");
            uuidAuthenticate(uuid).then(() => {
                if (dpid !== null) {
                    // redirect the user back to page without uuid parameter
                    navigate("/jobsearch");
                    handleDpidSearch(dpid);
                    getOpenInvoicesByGovid().catch(error => {
                        setGovidJobs(error);
                    });
                } else if (jobTokenUuid) {
                    // Not sure when this would be used together with uuid param, but just in case
                    handleSubmit(jobTokenUuid, true);
                    clearjobTokenUuid();
                } else {
                    // redirect the user back to page without uuid parameter
                    navigate("/jobsearch");

                    // fetch jobs by govid (hetu)
                    getOpenInvoicesByGovid().catch(error => {
                        setGovidJobs(error);
                    });

                    toggleLoading(false);

                    // User has identificated, allow access to / to display the page
                    setIsAuthenticating(false);
                }
            }).catch(_error => {
                // token request failed, redirecting back to indentification
                navigate("/logout");
                toggleLoading(false);
            });
        } else if (userIsLoggedIn()) {
            // get the company name from the query parameters if available
            const queryParams = new URLSearchParams(location.search);
            const companyName = queryParams.get('s');

            // Allow to display the page
            setIsAuthenticating(false);

            if (dpid !== null && !userShouldSwitchToCompany) {
                handleDpidSearch(dpid);
            } else if (jobTokenUuid && !userShouldSwitchToCompany) {
                handleSubmit(jobTokenUuid, true);
                clearjobTokenUuid();
            }

            if (companyName !== null) {
                searchInUrlParams = true;
                // search invoices by company name
                getOpenInvoicesByGovid(userAuthorized ? 'search' : 'linksearch', companyName).catch(error => {
                   setGovidJobs(error);
                });
                setSearchText(companyName);
            } else if (userAuthorized && !userShouldSwitchToCompany) {
                // Fetch the open invoices by govid
                getOpenInvoicesByGovid().catch(error => {
                    setGovidJobs(error);
                });
            }
        }

    }, []);


    /**
     * Trigger when callback variable is set, e.g. when a query needs to be formed jobid and code
     * (page is rendered, or invoice details are opened from the list).
     */
    useEffect(() => {
        if (callback === 'dpid') {
            toggleLoading(false);
            sessionStorage.removeItem('dpid');
            handleSubmit(state.jobid + '-' + state.code);
        }
        else if (callback === 'link') {
            // create query from jobid and code and search it
            const query = state.jobid + '-' + state.code;
            handleSubmit(query);
        }

    }, [callback]);

    /**
     * Link to job using govid search
     *
     * @param event
     * @param dpid
     */
    function linkSubmit(event, dpid) {

        event.preventDefault();

        if (!isEmpty(dpid)) {
            const dpid_array = dpid.split('-');
            setState({
                ...state,
                jobid: dpid_array[0],
                code: dpid_array[1]
            });
            // set value to callback to trigger useEffect
            setCallback('link');
        }
    }

    const handleBankAccountSubmitSuccess = (dpid) => {
        handleSubmit(dpid);
    }

    /**
     * Submit the search form, and redirect to job (also used for govid search)
     *
     * @param query
     * @param useUuid
     */
    const handleSubmit = (query, useUuid = false) => {
        // Reset errors and other notifications
        resetRetrieveInvoiceError();
        resetDashboardMessage();
        resetOverpaidInformation();

        toggleLoading(true, lng(messages.searchingForJob));

        // API call GET /online/jobsearch/{dpid}, jobservice fetches the job details and saves it to storage for later use
        (useUuid ? fetchJobWithUuid(query) : jobFetchJob(query)).then(() => {
            toggleLoading(false);
            // Check the job status code
            const job = jobGetJob();
            // Check if job is overpayment or credit note
            if (job.statuscode === 302 || job.statuscode === 104) {
                // If job status is overpayment or credit note, ensure the Retrieve invoice modal is closed
                setShowRetrieveModal(false);

                // Check if bank account number has already been notified
                if (job.modoverpaid === true) {
                    setOverpaidJob(job);
                    setShowOverpaidWizard(true);
                } else {
                    setOverpaidInformationFromJob(job);
                    returnPaymentClose();
                }
            } else {
                // If job status is not overpayment or credit note, redirect to job's page
                navigate("/job");
            }
        }).catch((error) => {
            toggleLoading(false);
            // response exists
            if (error?.response !== undefined) {
                if (error.response.status === 401) {
                    // authentication failed, redirect back to authentication
                    const alert = { message: lng(messages.authError), type: 'danger' };
                    navigate("/logout", { state: { alert } });
                } else if (error.response.status === 403) {
                    // Forbidden messages
                    let message = lng(messages.forbiddenError);
                    const errorMessage = error.response.data?.Message;

                    if (errorMessage === 'Online access log - user access denied') {
                        message = lng(messages.searchLocked);
                    } else if (errorMessage === 'Online jobsearch - company person is not allowed to sign') {
                        message = lng(messages.notAllowedToSign);
                    }

                    setRetrieveInvoiceError({
                        message: message,
                        type: 'danger'
                    });
                } else if (useUuid) {
                    // Invoice not found via link — show top-level info, clean URL, load govid jobs
                    setDashboardMessage({ message: lng(messages.invoiceNotFoundFromLink), type: 'info' });
                    navigate("/jobsearch");
                    getOpenInvoicesByGovid().catch(error => {
                        setGovidJobs(error);
                    });
                } else {
                    setRetrieveInvoiceError({
                        message: lng(messages.jobNotFound),
                        type: 'danger'
                    });
                }
            } else {
                // request failed completely
                setRetrieveInvoiceError({
                    message: lng(messages.pleaseTryAgain),
                    type: 'danger'
                });
            }
        });
    }

    /**
     * Get overpaid information for job with notified account number
     * and set it to be displayed to user
     * @param {object} job
     */
    const setOverpaidInformationFromJob = (job) => {
        // Get locale and currency for formatting
        let currency = get(job, 'currency', false);
        currency = currency && currency !== '' ? currency : process.env.REACT_APP_DEFAULT_CURRENCY;

        setOverpaidInformation({
            show: true,
            account: job.overpaid_iban,
            amount: formatMoney(job.modoverpaidamount, userLocale, currency),
            name: job.overpaid_name,
            fee: formatMoney(job.modoverpaidfee, userLocale, currency)
        })
    }

    /**
     * Reset overpaid information
     */
    function resetOverpaidInformation() {
        setOverpaidInformation({
            show: false,
            account: '',
            amount: '',
            name: '',
            fee: ''
        });
    }

    /**
     * Reset general dashboard message
     */
    function resetDashboardMessage() {
        setDashboardMessage({
            message: '',
            type: 'light'
        });
    }

    /**
     * Reset invoice retrieving errors
     */
    function resetRetrieveInvoiceError() {
        setRetrieveInvoiceError({
            message: '',
            type: 'light'
        });
    }

    /**
     * Toggle retrieve invoice modal visibility and clear its errors
     */
    function retrieveModalToggle() {
        resetRetrieveInvoiceError();
        resetDashboardMessage();
        resetOverpaidInformation();
        setShowRetrieveModal(!showRetrieveModal);
    }

    /**
     * Toggle overpaid wizard visibility and clear its errors
     */
    const overpaidWizardToggle = () => {
        resetRetrieveInvoiceError();
        resetDashboardMessage();
        resetOverpaidInformation();
        if (showOverpaidWizard) {
            setOverpaidJob(null);
        }
        setShowOverpaidWizard(!showOverpaidWizard);
    }

    /**
     * Handle search input events
     * @param event
     */
    function handleSearch(event) {
        setSearchText(event.target.value);
        debounced(event.target.value);
    }

    if (isAuthenticating) {
        return <></>
    }

    return (
        <div className='content flex-fill'>
            <Helmet id='job-helmet'>
                <title>{lng(messages.searchTitle)} | MyRopo</title>
            </Helmet>
            <Container fluid id='dashboard' className='p-0 d-flex h-100'>
                <Container className="p-4 pb-0">
                    <main id="main">
                        {userAuthorized && (
                            <DashboardHeader
                                dashboardMessage={dashboardMessage}
                                overpaidInformation={overpaidInformation}
                                retrieveModalToggle={retrieveModalToggle}
                                overpaidWizardToggle={overpaidWizardToggle}
                                showChatDisabledNotice={showChatDisabledNotice}

                            />
                        )}
                        <Tab.Container id="govidsearch" defaultActiveKey="invoices" activeKey={currentTab} onSelect={(key) => setCurrentTab(key)}>
                            <Row className={userAuthorized ? "pt-4" : "pt-md-4"}>
                                <Col xl={userAuthorized ? 6 : 12} md={12} className="order-2 order-xl-1 mt-4 mt-xl-0 online-tabs">
                                    {currentTab === "search" ? (
                                        <Nav
                                            fill
                                            defaultActiveKey="search"
                                            className="online-tabs"
                                            role="tablist"
                                            aria-label={lng(messages.searchResults)}
                                        >
                                            <Nav.Item>
                                                <Nav.Link
                                                    eventKey="search"
                                                >
                                                    {lng(messages.searchResults)}
                                                </Nav.Link>
                                            </Nav.Item>
                                        </Nav>
                                    ) : (
                                        <Nav
                                            justify
                                            defaultActiveKey="invoices"
                                            className="online-tabs"
                                            onSelect={(selectedKey) => getOpenInvoicesByGovid(selectedKey)}
                                            role="tablist"
                                            aria-label={lng(messages.tabInvoicesTitle) + " & " + lng(messages.tabArchiveTitle)}
                                        >
                                            <Nav.Item>
                                                <Nav.Link
                                                    eventKey="invoices"
                                                    id="tab-invoices"
                                                    aria-controls="tabpanel-invoices"
                                                    aria-selected={currentTab === "invoices"}
                                                    tabIndex={0}
                                                    role="tab"
                                                >
                                                    {lng(messages.tabInvoicesTitle)}
                                                </Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link
                                                    eventKey="archive"
                                                    id="tab-archive"
                                                    aria-controls="tabpanel-archive"
                                                    aria-selected={currentTab === "archive"}
                                                    tabIndex={0}
                                                    role="tab"
                                                >
                                                    {lng(messages.tabArchiveTitle)}</Nav.Link>
                                            </Nav.Item>
                                        </Nav>
                                    )}
                                </Col>
                                <div className='d-md-none' ref={searchResultsRef} />
                                {/* Search field */}
                                {userAuthorized && (
                                    <Col xl={6} md={12} className="order-1 order-xl-2">
                                        <InputGroup className="has-search">
                                            <InputGroup.Text className="form-control-search" id="search-icon">
                                                <FontAwesomeIcon className="text-body-tertiary" icon={far.faMagnifyingGlass} />
                                            </InputGroup.Text>
                                            <Form.Control
                                                placeholder={lng(messages.searchFieldPlaceholder)}
                                                aria-label={lng(messages.searchFieldPlaceholder)}
                                                aria-describedby={lng(messages.searchFieldPlaceholder)}
                                                onChange={handleSearch}
                                                value={searchText}
                                                id="dashboard-search-input"
                                                type="search"
                                                autoComplete="off"
                                                data-testid="dashboard-search-input"
                                            />
                                        </InputGroup>
                                    </Col>
                                )}
                            </Row>
                            <Row>
                                <Col>
                                    <Tab.Content>
                                        <DashboardPane
                                            current={currentTab === 'invoices'}
                                            jobs={govidJobs}
                                            type="invoices"
                                            onClick={linkSubmit}
                                            emptyResultMessage={userIsCompany() ? lng(messages.noOpenCompany) : lng(messages.noOpen)}
                                        />
                                        <DashboardPane
                                            current={currentTab === 'archive'}
                                            jobs={govidJobs}
                                            type="archive"
                                            onClick={linkSubmit}
                                            emptyResultMessage={userIsCompany() ? lng(messages.noOpenCompany) : lng(messages.noArchivedInvoices)}
                                        />
                                        <DashboardPane
                                            current={currentTab === 'search'}
                                            jobs={govidJobs}
                                            type="search"
                                            onClick={linkSubmit}
                                            emptyResultMessage={userIsCompany() ? lng(messages.noOpenCompany) : lng(messages.noSearchResults)}
                                        />
                                    </Tab.Content>
                                </Col>
                            </Row>
                        </Tab.Container>
                        {/* Users with direct link see log in button */}
                        {!userAuthorized && (
                            <Row className="sticky-bottom pb-3">
                                <Col xs={12} xl={{ span: 6, offset: 3 }} className="d-grid ">
                                    <Button variant="primary" onClick={() => navigate("/user/logout")}>{lng(messages.logInForAllInvoices)}</Button>
                                </Col>
                            </Row>
                        )}
                    </main>
                </Container>
                <RetrieveInvoiceModal
                    show={showRetrieveModal}
                    hide={retrieveModalToggle}
                    handleSubmit={handleSubmit}
                    error={retrieveInvoiceError}
                    {...props}
                />
                <OverpaidWizard
                    open={showOverpaidWizard}
                    onClose={overpaidWizardToggle}
                    job={overpaidJob}
                    onJobSearchSubmit={handleOverpaidJobSearch}
                    showSearchStep={true}
                    jobSearchError={retrieveInvoiceError}
                    onBankAccountSubmitSuccess={handleBankAccountSubmitSuccess}
                />
            </Container>
        </div>
    );
};

Dashboard.propTypes = {
    'intl': PropTypes.object.isRequired,
    'timeout': PropTypes.number,
};

export default injectIntl(Dashboard);
